import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Zap, Clock, ArrowRight, Scale } from 'lucide-react';
import { API } from '../App';

const VERDICT_COLORS = { Pass: '#22C55E', Conditional: '#F59E0B', Fail: '#EF4444' };

export default function SharePage() {
  const { shareToken } = useParams();
  const [fray, setFray] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    API.get(`/api/share/${shareToken}`)
      .then(r => setFray(r.data))
      .catch(err => setError(err.response?.data?.detail || 'Fray not found'))
      .finally(() => setLoading(false));
  }, [shareToken]);

  if (loading) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center">
      <div className="w-6 h-6 border-2 border-[#FF4D00] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-[#0F0F0F] flex items-center justify-center px-6">
      <div className="text-center">
        <Scale className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
        <h1 className="text-xl font-bold font-['Barlow_Condensed'] mb-2">Fray Not Found</h1>
        <p className="text-[#A0A0A0] text-sm mb-6">{error}</p>
        <Link to="/" className="px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black text-sm font-medium inline-flex items-center justify-center">Go to Fray</Link>
      </div>
    </div>
  );

  const verdict = fray?.verdict || {};

  return (
    <div className="min-h-screen bg-[#0F0F0F]" data-testid="share-page">
      {/* Header */}
      <div className="border-b border-[#2A2A2A] px-4 md:px-6 py-4">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-6 h-6 bg-[#FF4D00] flex items-center justify-center" style={{ clipPath: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)' }}>
              <Zap className="w-3.5 h-3.5 text-black" />
            </div>
            <span className="text-sm font-semibold font-['Barlow_Condensed']">Fray</span>
          </Link>
          <span className="text-xs font-mono text-[#A0A0A0]">{fray?.template_name}</span>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 md:px-6 py-8 md:py-12">
        {/* Verdict Badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.42 }}
          className="p-4 md:p-8 bg-[#1A1A1A] border-2 mb-6 md:mb-8 text-center"
          style={{ borderColor: VERDICT_COLORS[verdict.verdict] || '#FF4D00' }}
          data-testid="share-verdict-badge"
        >
          <span className="text-xs font-mono text-[#A0A0A0] block mb-2">FINAL CALL</span>
          <h1 className="text-3xl md:text-5xl font-bold font-['Barlow_Condensed'] mb-3"
            style={{ color: VERDICT_COLORS[verdict.verdict] || '#FF4D00' }}>
            {verdict.verdict || 'Pending'}
          </h1>
          <span className="text-xs font-mono text-[#A0A0A0] block mb-4">
            Confidence: {verdict.verdict_confidence || 'N/A'}
          </span>
          <p className="text-[#A0A0A0] text-sm leading-relaxed max-w-lg mx-auto">{verdict.executive_summary}</p>

          <div className="flex flex-wrap items-center justify-center gap-3 md:gap-6 mt-6 text-xs font-mono text-[#A0A0A0]">
            {fray?.tension_score > 0 && (
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-[#FF4D00]" /> {fray.tension_score}% friction
              </span>
            )}
            {fray?.duration_seconds > 0 && (
              <span className="flex items-center gap-1">
                <Clock className="w-3 h-3" /> {Math.round(fray.duration_seconds / 60)}m {fray.duration_seconds % 60}s
              </span>
            )}
            <span>{fray?.voices?.length || 0} Voices</span>
          </div>
        </motion.div>

        {/* Hot take */}
        {verdict.hot_take_quote && (
          <div className="p-4 bg-[#FF4D00]/5 border border-[#FF4D00]/20 mb-8">
            <Zap className="w-3 h-3 text-[#FF4D00] inline mr-1" />
            <span className="text-xs font-mono text-[#FF4D00]">HOT TAKE</span>
            <p className="text-sm text-[#F5F5F5] mt-1 italic">"{verdict.hot_take_quote}"</p>
          </div>
        )}

        {/* Scenario (truncated) */}
        <div className="p-4 bg-[#1A1A1A] border border-[#2A2A2A] mb-8">
          <span className="text-xs font-mono text-[#A0A0A0] block mb-1">SCENARIO</span>
          <p className="text-sm text-[#F5F5F5]">{fray?.scenario}</p>
        </div>

        {/* Committee */}
        <div className="mb-8">
          <span className="text-xs font-mono text-[#A0A0A0] block mb-3">COMMITTEE</span>
          <div className="flex flex-wrap gap-2">
            {fray?.voices?.map((v, i) => (
              <span key={i} className="text-[10px] px-2 py-1 border border-[#2A2A2A] font-mono" style={{ color: v.color }}>
                {v.name}
              </span>
            ))}
          </div>
        </div>

        {/* Risk register preview */}
        {verdict.risk_register?.length > 0 && (
          <div className="mb-8">
            <span className="text-xs font-mono text-[#A0A0A0] block mb-3">KEY RISKS</span>
            <div className="space-y-2">
              {verdict.risk_register.slice(0, 3).map((r, i) => (
                <div key={i} className="p-3 bg-[#1A1A1A] border border-[#2A2A2A] text-sm">
                  <span className="text-[9px] font-mono px-1.5 py-0.5 border mr-2 capitalize"
                    style={{ color: r.severity === 'critical' || r.severity === 'high' ? '#EF4444' : '#F59E0B',
                             borderColor: (r.severity === 'critical' || r.severity === 'high' ? '#EF4444' : '#F59E0B') + '40' }}>
                    {r.severity}
                  </span>
                  {r.risk}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="p-4 md:p-6 bg-[#1A1A1A] border border-[#FF4D00]/30 text-center">
          <h3 className="text-lg font-semibold font-['Barlow_Condensed'] mb-2">Start Your Own Fray</h3>
          <p className="text-sm text-[#A0A0A0] mb-4">Watch AI Voices debate your decision in real-time.</p>
          <Link to="/signup" className="inline-flex items-center justify-center gap-2 px-6 py-3 min-h-[44px] bg-[#FF4D00] text-black font-medium text-sm" data-testid="share-cta">
            Start Free Fray <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
